import { createAction } from "redux-actions";
import { takeLatest, put, select, call } from "redux-saga/effects";
import { queryParams } from "../../../../utils/queryParams";
import axios from "axios";

export const SET_UPDATES_LOADING = "saga/SET_UPDATES_LOADING";
export const SET_FILTERS_DIRTY = "saga/SET_FILTERS_DIRTY";
export const SET_LOADING = "saga/SET_LOADING";
export const SET_TOGGLE = "saga/HANDLE_TOGGLE";
export const SET_FILTERS_LOADING = "saga/SET_FILTERS_LOADING";

export const FETCH_CURRENT_USER_SUCCESS = "saga/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_REVENUE_CATEGORIES_SUCCESS = "saga/FETCH_REVENUE_CATEGORIES_SUCCESS";
export const FETCH_FILTERS_SUCCESS = "saga/FETCH_FILTERS_SUCCESS";
export const FETCH_TABLE_SUCCESS = "saga/FETCH_TABLE_SUCCESS";
export const FETCH_CHART_SUCCESS = "saga/FETCH_CHART_SUCCESS";
export const FETCH_CURRENT_USER = "saga/FETCH_CURRENT_USER";

export const UPDATE_DATA = "saga/UPDATE_DATA";
export const UPDATE_REVENUE_CATEGORIES = "saga/UPDATE_REVENUE_CATEGORIES";
export const UPDATE_DATES = "saga/UPDATE_DATES";
export const UPDATE_CUSTOM_RANGE = "saga/UPDATE_CUSTOM_RANGE";
export const UPDATE_FILTERS = "saga/UPDATE_FILTERS";
export const UPDATE_VIEW_FILTER = "saga/UPDATE_VIEW_FILTER";

export const RESET_DATA = "saga/RESET_DATA";

const API_URL = "pages/sessions_page/remaining_sessions";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  resetData: createAction(RESET_DATA),
  updateData: createAction(UPDATE_DATA),
  updateCategories: createAction(UPDATE_REVENUE_CATEGORIES),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  updateFilters: createAction(UPDATE_FILTERS),
  updateViewFilter: createAction(UPDATE_VIEW_FILTER)
};

export default function* watchActiveMembers() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_DATA, updateData);
  yield takeLatest(UPDATE_REVENUE_CATEGORIES, getRevenueCategories);
}

export function* fetchCurrentUserData({ payload }) {
  const { currencySymbol, useSecondaryCategories } = yield select(
    (state) => state.commonReducer.commonProps
  );
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: { ...payload, currencySymbol, useSecondaryCategories }
  });

  yield put({ type: SET_LOADING, payload: true });
  try {
    yield fetchFilters();
    yield fetchChart();
    yield fetchTableData();

    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* fetchChart() {
  const {
    commonParams,
    remaining_sessions,
    membership_types,
    view,
    prospect_types,
    dynamicParams
  } = yield getCommonApiParams();
  if (
    (view === "Membership Type" && !membership_types.length) ||
    (view === "Prospect Type" && !prospect_types.length)
  ) {
    return;
  }

  const params = {
    ...commonParams,
    view,
    remaining_sessions: remaining_sessions.map((obj) => obj).join(","),
    ...dynamicParams
  };

  yield put({ type: SET_UPDATES_LOADING, payload: true });
  try {
    let { data } = yield call(
      axios.post,
      queryParams.apiVersion + `${API_URL}/get_pie_chart_values`,
      params
    );

    yield put({
      type: FETCH_CHART_SUCCESS,
      payload: data
    });

    yield put({ type: SET_UPDATES_LOADING, payload: false });
    yield put({ type: SET_FILTERS_DIRTY, payload: false });
  } catch (err) {
    console.error(err);
  }
}

function* updateData() {
  try {
    yield fetchChart();
    yield fetchTableData();
  } catch (error) {
    console.error(error);
  }
}

function* fetchTableData() {
  const {
    commonParams,
    remaining_sessions,
    membership_types,
    view,
    prospect_types,
    dynamicParams
  } = yield getCommonApiParams();

  if (
    (view === "Membership Type" && !membership_types.length) ||
    (view === "Prospect Type" && !prospect_types.length)
  ) {
    return;
  }

  yield put({ type: SET_UPDATES_LOADING, payload: true });

  const params = {
    ...commonParams,
    remaining_sessions: remaining_sessions.map((obj) => obj).join(","),
    view,
    ...dynamicParams
  };

  try {
    let { data: tableData } = yield call(
      axios.post,
      queryParams.apiVersion + `${API_URL}/get_table_values`,
      params
    );

    yield put({
      type: FETCH_TABLE_SUCCESS,
      payload: tableData
    });

    yield put({ type: SET_UPDATES_LOADING, payload: false });
    yield put({ type: SET_FILTERS_DIRTY, payload: false });
  } catch (err) {
    console.error(err);
  }
}

function* getRevenueCategories({ payload: { filter, data } }) {
  const { commonParams } = yield getCommonApiParams();
  yield put({ type: SET_FILTERS_LOADING, payload: { filter, isLoading: true } });
  yield put({ type: UPDATE_FILTERS, payload: { [filter]: data } });

  const params = {
    ...commonParams,
    [filter]: data.map(({ id }) => id).join(",")
  };
  try {
    let { data } = yield call(
      axios.post,
      queryParams.apiVersion + `${API_URL}/get_revenue_categories`,
      params
    );

    yield put({ type: FETCH_REVENUE_CATEGORIES_SUCCESS, payload: data });
    yield put({ type: SET_FILTERS_LOADING, payload: { filter, isLoading: false } });
  } catch (error) {
    console.error(error);
  }
}

function* fetchFilters() {
  const { commonParams: params } = yield getCommonApiParams();

  try {
    let { data } = yield call(
      axios.post,
      queryParams.apiVersion + `${API_URL}/get_filters`,
      params
    );
    yield put({
      type: FETCH_FILTERS_SUCCESS,
      payload: data
    });
  } catch (err) {
    console.error(err);
  }
}

function* getCommonApiParams() {
  const { organization_id, short_url, useSecondaryCategories } = yield select(
    (state) => state.commonReducer.commonProps
  );
  return yield select(({ sessions: { selectedFilters } }) => {
    const {
      membership_types,
      remaining_sessions,
      view,
      prospect_types,
      group_by,
      primary_categories,
      secondary_categories,
      sales_packages
    } = selectedFilters;

    const dynamicParams = {
      ...(view === "Membership Type"
        ? { membership_types: membership_types.map(({ name }) => name) }
        : { prospect_types: prospect_types.map(({ name }) => name) }),
      ...(view === "Category" && {
        primary_categories: useSecondaryCategories
          ? primary_categories?.map(({ id }) => id).join(",")
          : undefined,
        secondary_categories: secondary_categories?.map(({ id }) => id).join(","),
        sales_packages: sales_packages?.map(({ id }) => id).join(","),
        group_by: group_by.id
      })
    };

    return {
      commonParams: {
        organization_id: organization_id.toString(),
        short_url
      },
      membership_types,
      view,
      prospect_types,
      remaining_sessions,
      dynamicParams
    };
  });
}
